/**
 * GatedTabContent — Access control wrapper for in-page tabs
 *
 * Wraps individual tab panels and checks the user's role against
 * the SUB_TAB_REGISTRY before rendering content. If the user doesn't
 * have access, shows a permission denied message or hides the tab entirely.
 *
 * Usage with shadcn Tabs:
 *   <TabsContent value="financials">
 *     <GatedTabContent pageKey="health-plan-detail" tabKey="employers">
 *       <EmployerContractsTab />
 *     </GatedTabContent>
 *   </TabsContent>
 *
 * Usage with custom tabs:
 *   {activeTab === 'config' && (
 *     <GatedTabContent pageKey="rag-admin" tabKey="config">
 *       <ConfigPanel />
 *     </GatedTabContent>
 *   )}
 *
 * Usage for tab trigger visibility:
 *   <GatedTabTrigger pageKey="health-plan-detail" tabKey="employers">
 *     <TabsTrigger value="employers">Employer Contracts</TabsTrigger>
 *   </GatedTabTrigger>
 */

import React, { useMemo } from 'react';
import { useRole } from '@/contexts/RoleContext';
import { SUB_TAB_REGISTRY, type SubTabRule, checkSubTabAccess } from '@/utils/subTabRegistry';
import { ShieldAlert } from 'lucide-react';

interface GatedTabContentProps {
  /** The page this tab belongs to (key in SUB_TAB_REGISTRY) */
  pageKey: string;
  /** The tab key within that page */
  tabKey: string;
  /** Content to render if access is granted */
  children: React.ReactNode;
  /** What to show when access is denied: 'message' shows a notice, 'hide' renders nothing */
  denyBehavior?: 'message' | 'hide';
}

export function GatedTabContent({
  pageKey,
  tabKey,
  children,
  denyBehavior = 'message',
}: GatedTabContentProps) {
  const { currentRole } = useRole();

  const access = useMemo(
    () => checkSubTabAccess(pageKey, tabKey, currentRole),
    [pageKey, tabKey, currentRole]
  );

  if (!access.allowed) {
    if (denyBehavior === 'hide') {
      return null;
    }
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-400">
        <ShieldAlert className="w-10 h-10 mb-3" />
        <p className="text-sm font-medium">Access Restricted</p>
        <p className="text-xs mt-1 max-w-xs text-center">
          {access.reason || 'You do not have permission to view this section.'}
        </p>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * GatedTabTrigger — Hides tab trigger buttons the user can't access.
 * Wrap around TabsTrigger or custom tab buttons.
 */
interface GatedTabTriggerProps {
  pageKey: string;
  tabKey: string;
  children: React.ReactNode;
}

export function GatedTabTrigger({ pageKey, tabKey, children }: GatedTabTriggerProps) {
  const { currentRole } = useRole();

  const access = useMemo(
    () => checkSubTabAccess(pageKey, tabKey, currentRole),
    [pageKey, tabKey, currentRole]
  );

  if (!access.allowed) {
    return null;
  }

  return <>{children}</>;
}

/**
 * useSubTabAccess — Hook to check if current user can access a specific sub-tab.
 * Useful when you need programmatic access checks (not JSX wrapping).
 */
export function useSubTabAccess(pageKey: string, tabKey: string): {
  allowed: boolean;
  reason?: string;
} {
  const { currentRole } = useRole();
  return useMemo(
    () => checkSubTabAccess(pageKey, tabKey, currentRole),
    [pageKey, tabKey, currentRole]
  );
}

/**
 * usePageSubTabs — Returns the list of sub-tabs the current user can access
 * for a given page. Useful for building filtered tab lists.
 */
export function usePageSubTabs(pageKey: string): string[] {
  const { currentRole } = useRole();
  return useMemo(() => {
    const page = SUB_TAB_REGISTRY[pageKey];
    if (!page) return [];
    return Object.entries(page)
      .filter(([_, rule]) => {
        if (rule.public) return true;
        if (!currentRole) return false;
        if (rule.roles && rule.roles.length > 0) {
          return rule.roles.includes(currentRole);
        }
        return true;
      })
      .map(([key]) => key);
  }, [pageKey, currentRole]);
}
